import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { reduxForm, formValueSelector } from 'redux-form';
import { withCandidateJwt } from '../../../../../../../../hooks/withCandidateJwt';
import { withCandidateStorySubmission } from '../../../../../../../../hooks/withCandidateStorySubmission';
import { connect } from 'react-redux';
import {
  getCandidateStoryPrompts,
  getCandidateStoryRequests,
} from 'api/adjudication';
import LoadingOverlay from './LoadingOverlay';
import {
  CANDIDATE_STORY_FORM_NAME,
  CANDIDATE_STORY_SECTIONS,
  MAX_DOCUMENTS,
} from '../../../../../../../../constants';
import * as V from '../../../../../../../../lib/validations/candidateStoryValidations';
import { getSearches } from '../../../../../lib/screenings';
import scrollToTop from '../../../../../../../../utils/scrollToTop';
import ChargesForm from './ChargesForm/Container';
import ConfirmForm from './ConfirmForm';
import GeneralInformationForm from './InformationForms/GeneralInformationForm/Container';
import CaseInformationForm from './InformationForms/CaseInformationForm/Container';
import CandidateStorySummary from './CandidateStorySummary/Container';
import Footer from './Footer';
import {
  getGeneralPromptGroup,
  getCriminalPromptGroup,
  isGeneralEnabled,
  isRecordsEnabled,
  isSettingEnabled,
  getRequestedRecords,
  hasFillablePrompts,
  isPromptItemEmpty,
} from '../../../../helpers';
import DocumentValidationNotification from './DocumentValidationNotification';
import EmptyItemNotification from './EmptyItemNotification';
import { CandidateStoryFormContext } from './Context';
import TrackStepViewed from '../../../../../../../Workflow/TrackStepViewed';
import { ANALYTICS_EVENTS } from '../../../../../../../../lib/analytics';
import '../../../../../../../../scss/CandidateStories.scss';

class CandidateStoryForm extends Component {
  constructor(props) {
    super(props);

    this.switchToDisputeModal = this.switchToDisputeModal.bind(this);
    this.confirmAccuracy = this.confirmAccuracy.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.completeInformationSelection =
      this.completeInformationSelection.bind(this);
    this.toggleGeneralInformation = this.toggleGeneralInformation.bind(this);
    this.toggleRecord = this.toggleRecord.bind(this);
    this.completeRecordSelection = this.completeRecordSelection.bind(this);
    this.remove = this.remove.bind(this);
    this.findIndexOfRecord = this.findIndexOfRecord.bind(this);
    this.switchToChargeSelection = this.switchToChargeSelection.bind(this);
    this.passAcknowledged = this.passAcknowledged.bind(this);
    this.fetchPromptGroups = this.fetchPromptGroups.bind(this);
    this.scrollToModalTop = this.scrollToModalTop.bind(this);
    this.searches = [];
    this.state = { isLoading: true };
    this.contextValue = { chargeSelectionEnabled: true };
  }

  scrollToModalTop() {
    setTimeout(() => scrollToTop('.mastodon-modal-body'), 150);
  }

  componentDidMount() {
    this.setup();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  async setup() {
    const { report } = this.props;

    this.searches = getSearches(report, CANDIDATE_STORY_SECTIONS);

    // The requests can outlive the modal, so stop once it is gone.
    await this.setupPrompts();
    if (this.unmounted) return;
    await this.setupRequest();
    if (this.unmounted) return;
    this.setupGeneralInformation();
    this.setState({ isLoading: false });
  }

  setupGeneralInformation() {
    if (this.isGeneralInformationOnly()) {
      this.toggleGeneralInformation();
    }
  }

  isGeneralInformationOnly() {
    const {
      generalInformationOnly,
      report: { account },
    } = this.props;
    return (
      generalInformationOnly ||
      (isGeneralEnabled(account, this.generalPromptGroup) &&
        !isRecordsEnabled(account, this.criminalPromptGroup))
    );
  }

  async setupPrompts() {
    await this.fetchPromptGroups();
  }

  async fetchPromptGroups() {
    const {
      change,
      report: { account },
    } = this.props;
    try {
      const promptGroupsResponse = await getCandidateStoryPrompts(account.id);
      if (this.unmounted) return;
      this.promptGroups = promptGroupsResponse.data;
      change('promptGroups', this.promptGroups);

      if (isSettingEnabled('GeneralInfo', account)) {
        this.generalPromptGroup = getGeneralPromptGroup(this.promptGroups);
      }
      if (isSettingEnabled('RecordsInfo', account)) {
        this.criminalPromptGroup = getCriminalPromptGroup(this.promptGroups);
      }
    } catch {
      if (this.unmounted) return;
      this.promptGroups = [];
      change('promptGroups', []);
    }
  }

  async setupRequest() {
    const request = await this.fetchRequest();
    if (this.unmounted) return;
    this.contextValue = {
      ...this.contextValue,
      chargeSelectionEnabled: !request?.required,
      requestedItems: request?.report_items,
    };
    this.preselectRequestedRecords();
  }

  async fetchRequest() {
    const { report } = this.props;
    try {
      const requestData = await getCandidateStoryRequests(report.id);
      return requestData;
    } catch {
      return null;
    }
  }

  preselectRequestedRecords() {
    const { change } = this.props;
    const requestedRecords = getRequestedRecords(
      this.searches,
      this.contextValue.requestedItems,
    );
    change('selectedRecords', requestedRecords);
    change('selectedItems', requestedRecords.length);
  }

  switchToDisputeModal() {
    const { toggleCandidateStoryModal, toggleDisputeModal } = this.props;
    toggleDisputeModal();
    toggleCandidateStoryModal();
  }

  confirmAccuracy() {
    const { change } = this.props;

    change('confirmed', true);
  }

  handleBack() {
    const {
      change,
      caseInformationComplete,
      confirmed,
      currentCaseId,
      generalInformation,
      generalInformationComplete,
      recordSelectionComplete,
      selectedRecords,
    } = this.props;

    change('acknowledged', false);

    if (caseInformationComplete) {
      change('caseInformationComplete', false);
    } else if (generalInformationComplete) {
      change('generalInformationComplete', false);
    } else if (
      selectedRecords.length > 0 &&
      generalInformationComplete &&
      !caseInformationComplete
    ) {
      const index = this.findIndexOfRecord(currentCaseId);
      const prev = index - 1 >= 0 ? index - 1 : -1;
      if (prev >= 0) {
        change('currentCaseId', selectedRecords[prev].id);
      } else if (generalInformation) {
        change('generalInformationComplete', false); // back to general information
      } else {
        change('recordSelectionComplete', false); // back to record selection
      }
    } else if (
      recordSelectionComplete &&
      generalInformation &&
      !generalInformationComplete
    ) {
      change('recordSelectionComplete', false); // back to charges selection
    } else if (recordSelectionComplete) {
      change('recordSelectionComplete', false);
    } else if (confirmed) {
      change('confirmed', false);
    }
    this.scrollToModalTop();
  }

  completeInformationSelection() {
    const {
      change,
      currentCaseId,
      generalInformation,
      generalInformationComplete,
      selectedRecords,
    } = this.props;

    if (generalInformation && !generalInformationComplete) {
      change('generalInformationComplete', true);
    } else if (selectedRecords.length) {
      const index = this.findIndexOfRecord(currentCaseId);
      if (selectedRecords[index + 1]) {
        change('currentCaseId', selectedRecords[index + 1].id);
      } else {
        change('caseInformationComplete', true);
      }
    } else {
      // move directly to summary screen
      change('generalInformationComplete', true);
    }
    this.scrollToModalTop();
  }

  toggleGeneralInformation() {
    const { change, candidateStories, generalInformation, selectedItems } =
      this.props;

    if (generalInformation) {
      const newCandidateStory = { ...candidateStories };
      delete newCandidateStory.content?.general;
      delete newCandidateStory.general;

      change('candidateStories', newCandidateStory);
      change('selectedItems', selectedItems > 0 ? selectedItems - 1 : 1);
    } else {
      change('selectedItems', selectedItems + 1);
    }
    change('generalInformation', !generalInformation);
  }

  toggleRecord(record) {
    const { array, change, candidateStories, selectedItems } = this.props;
    const i = this.findIndexOfRecord(record.id);

    if (i === -1) {
      array.push('selectedRecords', record);
      change('selectedItems', selectedItems + 1);
    } else {
      array.remove('selectedRecords', i);
      change('selectedItems', selectedItems - 1);

      const newCandidateStory = { ...candidateStories };
      delete newCandidateStory.content?.[record.id];
      delete newCandidateStory[record.id];
      change('candidateStories', newCandidateStory);
    }
  }

  completeRecordSelection() {
    const { change, selectedRecords } = this.props;
    change('recordSelectionComplete', true);
    if (selectedRecords.length) {
      change('currentCaseId', selectedRecords[0].id); // set the first case
    }
    this.scrollToModalTop();
  }

  remove(i, model) {
    const { array } = this.props;
    array.remove(model, i);
  }

  findIndexOfRecord(recordId) {
    const { selectedRecords } = this.props;
    return selectedRecords.findIndex(item => item.id === recordId);
  }

  passAcknowledged() {
    const { change } = this.props;
    change('acknowledged', true);
  }

  // A prompt-form item the candidate could fill in but left with no text and
  // no files would be rejected by the API, so it must block Continue/Send.
  isPromptItemBlank(key, promptGroup, setting) {
    const { candidateStories } = this.props;
    return (
      hasFillablePrompts(setting, promptGroup) &&
      isPromptItemEmpty(key, candidateStories, this.promptGroups)
    );
  }

  hasEmptyPromptItem(currentPage, generalInfoEnabled) {
    const { currentCaseId, generalInformation, report, selectedRecords } =
      this.props;
    const { candidateStoriesGeneralInfo, candidateStoriesRecordsInfo } =
      report.account;

    const generalIsBlank = () =>
      this.isPromptItemBlank(
        'general',
        this.generalPromptGroup,
        candidateStoriesGeneralInfo,
      );
    const recordIsBlank = recordId =>
      this.isPromptItemBlank(
        recordId,
        this.criminalPromptGroup,
        candidateStoriesRecordsInfo,
      );

    if (currentPage === 'generalInformation') {
      return generalIsBlank();
    }
    if (currentPage === 'caseInformation') {
      // A record form only renders for the record matching currentCaseId. When
      // none matches (e.g. preselected records on a general-info-only flow),
      // Continue must stay enabled so it can advance to the first record.
      const hasCurrentRecord =
        !!currentCaseId &&
        selectedRecords.some(record => record.id === currentCaseId);
      return hasCurrentRecord && recordIsBlank(currentCaseId);
    }
    if (currentPage === 'summary') {
      return (
        (generalInformation && generalInfoEnabled && generalIsBlank()) ||
        selectedRecords.some(record => recordIsBlank(record.id))
      );
    }
    return false;
  }

  switchToChargeSelection() {
    const {
      change,
      generalInformationComplete,
      caseInformationComplete,
      recordSelectionComplete,
    } = this.props;

    if (caseInformationComplete) {
      change('caseInformationComplete', false);
    }
    if (generalInformationComplete) {
      change('generalInformationComplete', false);
    }
    if (recordSelectionComplete) {
      change('recordSelectionComplete', false);
    }
  }

  render() {
    const {
      acknowledged,
      companyName,
      confirmed,
      currentCaseId,
      selectedRecords,
      selectedItems,
      generalInformation,
      generalInformationComplete,
      handleSubmit,
      toggleDisputeModal,
      processing,
      recordSelectionComplete,
      report,
      submitCandidateStories,
      candidateJwt,
      valid,
      candidateStories,
      caseInformationComplete,
    } = this.props;

    const { isLoading } = this.state;

    if (isLoading) {
      return <LoadingOverlay />;
    }

    const generalInformationOnly = this.isGeneralInformationOnly();
    const generalInfoEnabled = isGeneralEnabled(
      report.account,
      this.generalPromptGroup,
    );
    const recordsInfoEnabled = isRecordsEnabled(
      report.account,
      this.criminalPromptGroup,
    );

    const formPages = [
      { name: 'confirmSelect', showIf: !confirmed },
      {
        name: 'chargeSelect',
        showIf:
          recordsInfoEnabled &&
          !recordSelectionComplete &&
          !generalInformationOnly,
      },
      {
        name: 'generalInformation',
        showIf:
          generalInfoEnabled &&
          generalInformation &&
          !generalInformationComplete,
      },
      {
        name: 'caseInformation',
        showIf: selectedRecords.length > 0 && !caseInformationComplete,
      },
      {
        name: 'summary',
        showIf: caseInformationComplete || generalInformationComplete,
      },
    ];
    const currentPage = formPages.find(page => page.showIf).name;

    const formActions = {
      completeRecordSelection: this.completeRecordSelection.bind(this),
      confirmAccuracy: this.confirmAccuracy.bind(this),
      handleBack: this.handleBack.bind(this),
      switchToDisputeModal: this.switchToDisputeModal.bind(this),
      completeInformationSelection:
        this.completeInformationSelection.bind(this),
    };

    const exceedsMaxDocuments =
      V.getTotalDocumentCount(candidateStories) > MAX_DOCUMENTS;

    const hasEmptyItem = this.hasEmptyPromptItem(
      currentPage,
      generalInfoEnabled,
    );

    const hasAnySelectedItems = selectedItems > 0;
    const chargeSelectValid = valid && hasAnySelectedItems && !hasEmptyItem;

    return (
      <CandidateStoryFormContext.Provider value={this.contextValue}>
        <form
          onSubmit={handleSubmit(values =>
            submitCandidateStories(values, candidateJwt),
          )}
        >
          <TrackStepViewed
            event={ANALYTICS_EVENTS.STORY_STEP_VIEWED}
            step={currentPage}
          />
          <div className='p-4' data-testid='candidate-story-form'>
            {currentPage === 'confirmSelect' && (
              <ConfirmForm
                companyName={companyName}
                toggleDisputeModal={toggleDisputeModal}
                actions={formActions}
              />
            )}

            {currentPage === 'chargeSelect' && (
              <ChargesForm
                searches={this.searches}
                toggleGeneralInformation={this.toggleGeneralInformation}
                toggleRecord={this.toggleRecord}
                generalInfoEnabled={generalInfoEnabled}
                generalInformation={generalInformation}
                selectedRecords={selectedRecords}
                companyName={companyName}
              />
            )}

            {currentPage === 'generalInformation' && (
              <GeneralInformationForm
                account={report.account}
                companyName={companyName}
                candidateStories={candidateStories}
                remove={this.remove}
                selectedItems={selectedItems}
                promptGroup={this.generalPromptGroup}
              />
            )}

            {currentPage === 'caseInformation' &&
              selectedRecords.map((record, i) => (
                <CaseInformationForm
                  account={report.account}
                  record={record}
                  companyName={companyName}
                  candidateStories={candidateStories}
                  remove={this.remove}
                  selectedRecords={selectedRecords}
                  selectedItems={selectedItems}
                  order={i + 1}
                  currentCaseId={currentCaseId}
                  generalInformation={generalInformation}
                  key={record.id}
                  promptGroup={this.criminalPromptGroup}
                />
              ))}

            {currentPage === 'summary' && (
              <CandidateStorySummary
                candidateStories={candidateStories}
                companyName={companyName}
                generalInformationOnly={generalInformationOnly}
                selectedRecords={selectedRecords}
                switchToChargeSelection={this.switchToChargeSelection}
                passAcknowledged={this.passAcknowledged}
                promptGroups={this.promptGroups}
                exceedsMaxDocuments={exceedsMaxDocuments}
              />
            )}
            <DocumentValidationNotification
              exceedsMaxDocuments={exceedsMaxDocuments}
            />
            <EmptyItemNotification hasEmptyItem={hasEmptyItem} />
          </div>
          <div className='tile-option-footer'>
            {' '}
            <Footer
              currentPage={currentPage}
              actions={formActions}
              processing={processing}
              valid={chargeSelectValid}
              selectedItems={selectedItems}
              acknowledged={acknowledged}
              exceedsMaxDocuments={exceedsMaxDocuments}
            />
          </div>
        </form>
      </CandidateStoryFormContext.Provider>
    );
  }
}

CandidateStoryForm.propTypes = {
  acknowledged: PropTypes.bool.isRequired,
  array: PropTypes.object.isRequired,
  caseInformationComplete: PropTypes.bool,
  change: PropTypes.func.isRequired,
  companyName: PropTypes.string.isRequired,
  confirmed: PropTypes.bool,
  currentCaseId: PropTypes.string,
  generalInformation: PropTypes.bool,
  generalInformationComplete: PropTypes.bool,
  generalInformationOnly: PropTypes.bool,
  handleSubmit: PropTypes.func.isRequired,
  toggleCandidateStoryModal: PropTypes.func.isRequired,
  processing: PropTypes.bool.isRequired,
  recordSelectionComplete: PropTypes.bool,
  report: PropTypes.object.isRequired,
  selectedItems: PropTypes.number,
  selectedRecords: PropTypes.array,
  submitCandidateStories: PropTypes.func.isRequired,
  candidateJwt: PropTypes.string,
  toggleDisputeModal: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  candidateStories: PropTypes.object,
};

CandidateStoryForm.defaultProps = {
  caseInformationComplete: false,
  confirmed: false,
  currentCaseId: '',
  generalInformation: true,
  generalInformationComplete: false,
  generalInformationOnly: false,
  recordSelectionComplete: false,
  selectedItems: 0,
  selectedRecords: [],
  candidateStories: {},
};

const ReduxCandidateStoryForm = reduxForm({
  form: CANDIDATE_STORY_FORM_NAME,
  shouldValidate: () => true,
})(CandidateStoryForm);

const selector = formValueSelector(CANDIDATE_STORY_FORM_NAME);

const mapStateToProps = (state, ownProps) => ({
  initialValues: {
    candidateStories: {},
    report: ownProps.report,
    promptGroups: [], // Initialize as empty array, will be populated on mount
    userActions: [],
  },
  caseInformationComplete: selector(state, 'caseInformationComplete') || false,
  confirmed: selector(state, 'confirmed') || false,
  currentCaseId: selector(state, 'currentCaseId') || '',
  fields: selector(state, 'fields') || [],
  generalInformation: selector(state, 'generalInformation') || false,
  generalInformationComplete:
    selector(state, 'generalInformationComplete') || false,
  recordSelectionComplete: selector(state, 'recordSelectionComplete') || false,
  selectedItems: selector(state, 'selectedItems') || 0,
  selectedRecords: selector(state, 'selectedRecords') || [],
  candidateStories: selector(state, 'candidateStories') || {},
  acknowledged: selector(state, 'acknowledged') || false,
});

export default withCandidateJwt(
  withCandidateStorySubmission(
    connect(mapStateToProps)(ReduxCandidateStoryForm),
  ),
);
